import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  InputGroup,
  InputRightElement,
  TableContainer,
  Spinner,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Flex,
  useToast,
  useDisclosure,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useState, useEffect } from "react";
import Navbar from "../../Navbar/Navbar";
import Sidebar from "../../Sidebar";
import axios from "axios";
import { FiMoreVertical, FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import { api_url } from "../../../url";
import AddProviderModal from "./AddProviderModal";
import EditProviderModal from "./EditProviderModal";

const SIDEBAR_WIDTH = "240px";
const ITEMS_PER_PAGE = 10;

export default function AllProviders() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [providers, setProviders] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const toast = useToast();

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchProviders();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search, page]);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = {
        page: page,
        limit: ITEMS_PER_PAGE,
      };

      if (search) {
        params.name = search;
      }

      const { data } = await axios.get(`${api_url}/admin/provider`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (data.success) {
        setProviders(data.data || []); // Adjust based on actual API response structure
        setTotalPages(data.pagination?.totalPages || 1);
      } else {
        setProviders([]);
      }
    } catch (error) {
      console.error("Failed to fetch providers", error);
      toast({
        title: "Error fetching providers",
        description: error.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this provider?")) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(`${api_url}/providers/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast({
          title: "Provider deleted",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        fetchProviders();
      } catch (error) {
        toast({
          title: "Error deleting provider",
          description: error.response?.data?.message || "Something went wrong",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleEdit = (provider) => {
    setSelectedProvider(provider);
    onEditOpen();
  };

  const handleSuccess = () => {
    fetchProviders();
  };

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box
        p={{ base: 4, md: 8 }}
        bg="#f8f9fd"
        minH="100vh"
        mt="80px"
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
      >
        {/* HEADER */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          gap={4}
          mb={6}
        >
          <Box>
            <Text fontSize="2xl" fontWeight="700" color="#344767">
              All Providers
            </Text>
          </Box>

          <Flex gap={4}>
            <InputGroup maxW={{ base: "100%", md: "300px" }}>
              <Input
                placeholder="Search Providers..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                bg="white"
                borderRadius="8px"
              />
              <InputRightElement color="gray.400">
                <SearchIcon />
              </InputRightElement>
            </InputGroup>

            <Button
              paddingX={9}
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={onAddOpen}
            >
              Add Provider
            </Button>
          </Flex>
        </Flex>

        {/* TABLE CONTAINER */}
        <Box
          bg="white"
          borderRadius="12px"
          boxShadow="sm"
          overflow="hidden"
          border="1px solid #f0f2f5"
        >
          <TableContainer>
            <Table variant="simple">
              <Thead bg="#1f15da">
                <Tr>
                  <Th color="white" py={4}>
                    Name
                  </Th>
                  <Th color="white" py={4}>
                    Slug
                  </Th>
                  <Th color="white" py={4}>
                    Provider ID
                  </Th>
                  <Th color="white" py={4}>
                    Aggregator
                  </Th>
                  <Th color="white" py={4}>
                    Status
                  </Th>
                  <Th color="white" py={4}>
                    Actions
                  </Th>
                </Tr>
              </Thead>

              <Tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8}>
                      <Spinner />
                    </Td>
                  </Tr>
                ) : providers.length === 0 ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={8}>
                      No providers found.
                    </Td>
                  </Tr>
                ) : (
                  providers.map((p) => (
                    <Tr key={p._id} _hover={{ bg: "#f8f9fa" }}>
                      <Td fontWeight="600">{p.name}</Td>
                      <Td fontSize="sm" color="gray.600">
                        {p.slug}
                      </Td>
                      <Td fontSize="sm">{p.providerId || "-"}</Td>
                      <Td fontSize="sm">{p.aggregator || "-"}</Td>
                      <Td>
                        <Badge colorScheme={p.status ? "green" : "red"}>
                          {p.status ? "Active" : "Inactive"}
                        </Badge>
                      </Td>
                      <Td>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<FiMoreVertical />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<FiEdit />}
                              onClick={() => handleEdit(p)}
                            >
                              Edit
                            </MenuItem>
                            <MenuItem
                              icon={<FiTrash2 />}
                              color="red.500"
                              onClick={() => handleDelete(p._id)}
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        {/* PAGINATION */}
        {!loading && totalPages > 1 && (
          <Flex justify="center" mt={8} gap={3}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage(page - 1)}
              isDisabled={page === 1}
              borderRadius="md"
              borderColor="gray.200"
              _hover={{ bg: "gray.50" }}
            >
              Prev
            </Button>

            {[...Array(totalPages)].map((_, i) => (
              <Button
                key={i}
                size="sm"
                onClick={() => setPage(i + 1)}
                bg={page === i + 1 ? "#5e72e4" : "white"}
                color={page === i + 1 ? "white" : "gray.600"}
                border="1px solid"
                borderColor={page === i + 1 ? "#5e72e4" : "gray.200"}
                _hover={{
                  bg: page === i + 1 ? "#4e62d4" : "gray.50",
                }}
                minW="40px"
                borderRadius="md"
              >
                {i + 1}
              </Button>
            ))}

            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage(page + 1)}
              isDisabled={page === totalPages}
              borderRadius="md"
              borderColor="gray.200"
              _hover={{ bg: "gray.50" }}
            >
              Next
            </Button>
          </Flex>
        )}
      </Box>

      <AddProviderModal
        isOpen={isAddOpen}
        onClose={onAddClose}
        onSuccess={handleSuccess}
      />
      {selectedProvider && (
        <EditProviderModal
          isOpen={isEditOpen}
          onClose={() => {
            onEditClose();
            setSelectedProvider(null);
          }}
          provider={selectedProvider}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
}
